import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useEffect, useMemo, useState } from "react";
import { Polygon, Polyline } from "react-leaflet";

import type { Coordinates } from "../../features/dt-basic-setup/single-building/model/types";

import { averageND } from "../../utils/dataManipulation";
import { DrawingMapCanvas } from "./DrawingMapCanvas";
import { PolygonVertexMarkers } from "./PolygonVertexMarkers";

export type CompletedPolygon = { id: string; points: [number, number][] };

export type LatLngPoint = [number, number];

export const OUTLINE_DRAFT_POLYGON_ID = "outline-draft";

export type OutlineDrawingMapProps = {
  center: Coordinates;
  completedPolygons?: CompletedPolygon[];
  drawingPolygonId?: null | string;
  isDrawing: boolean;
  isEditing: boolean;
  onAddPoint: (point: LatLngPoint) => void;
  onFinish: () => void;
  onSelectPolygon?: (id: string) => void;
  onUpdatePoint: (index: number, point: LatLngPoint) => void;
  selectedPolygonId?: null | string;
};

const LAYER_POLYGON_ACTIVE = {
  color: "var(--core-color-secondary-blue-500)",
  fillOpacity: 0.18,
  weight: 3,
};

const LAYER_POLYGON_INACTIVE = {
  color: "var(--core-color-secondary-blue-300)",
  fillOpacity: 0,
  weight: 2,
};

const LAYER_RUBBER_BAND = {
  color: "var(--core-color-secondary-blue-200)",
  dashArray: "8 6",
  interactive: false,
  lineCap: "round" as const,
  lineJoin: "round" as const,
  opacity: 0.95,
  weight: 2,
};

const SELECTED_POLYGON_PATH_OPTIONS = {
  color: "var(--core-color-mystic-red-600)",
  fillColor: "var(--core-color-mystic-red-600)",
  fillOpacity: 0.2,
};

function pathOptionsForPolygon(poly: CompletedPolygon, selectedPolygonId: null | string) {
  if (selectedPolygonId != null) {
    return poly.id === selectedPolygonId
      ? { ...LAYER_POLYGON_ACTIVE, ...SELECTED_POLYGON_PATH_OPTIONS }
      : LAYER_POLYGON_INACTIVE;
  }
  return LAYER_POLYGON_ACTIVE;
}

const PolygonWithoutVertexMarker = ({
  onSelectPolygon = () => {},
  poly,
  selectedPolygonId,
}: {
  onSelectPolygon?: (id: string) => void;
  poly: CompletedPolygon;
  selectedPolygonId: null | string;
}) => {
  const positions = poly.points;
  const pathOptions = pathOptionsForPolygon(poly, selectedPolygonId);
  const eventHandlers = onSelectPolygon
    ? {
        click: (e: L.LeafletMouseEvent) => {
          L.DomEvent.stopPropagation(e);
          onSelectPolygon(poly.id);
        },
      }
    : {};
  const commonProps = {
    eventHandlers,
    key: poly.id,
    pathOptions,
  };

  if (positions.length === 0) return null;
  if (positions.length === 1) {
    const p = positions[0];
    return <Polyline {...commonProps} key={commonProps.key} positions={[p, p]} />;
  }
  if (positions.length < 3) {
    return <Polyline {...commonProps} key={commonProps.key} positions={positions} />;
  }
  return <Polygon {...commonProps} key={commonProps.key} positions={positions} />;
};

export const OutlineDrawingMap = ({
  center,
  completedPolygons = [],
  drawingPolygonId,
  isDrawing,
  isEditing,
  onAddPoint,
  onFinish,
  onSelectPolygon,
  onUpdatePoint,
  selectedPolygonId = null,
}: OutlineDrawingMapProps) => {
  const [hoveredVertexIndex, setHoveredVertexIndex] = useState<null | number>(null);
  const [draggingVertexIndex, setDraggingVertexIndex] = useState<null | number>(null);
  const [drawCursor, setDrawCursor] = useState<LatLngPoint | null>(null);

  const mapCenter = useMemo((): [number, number] => {
    const first = completedPolygons[0];
    if (first?.points?.length) return averageND(first?.points) as [number, number];
    return [center.lat, center.lng];
  }, [center.lat, center.lng, completedPolygons]);

  const vertexPoints = useMemo((): LatLngPoint[] => {
    if (selectedPolygonId != null) {
      return completedPolygons.find((p) => p.id === selectedPolygonId)?.points ?? [];
    }
    if (completedPolygons.length === 1) {
      return completedPolygons[0].points;
    }
    return [];
  }, [completedPolygons, selectedPolygonId]);

  const draftVertexPoints = useMemo((): LatLngPoint[] => {
    if (!isDrawing) return [];
    if (drawingPolygonId !== undefined) {
      if (drawingPolygonId == null) return [];
      return completedPolygons.find((p) => p.id === drawingPolygonId)?.points ?? [];
    }
    if (selectedPolygonId != null) {
      return completedPolygons.find((p) => p.id === selectedPolygonId)?.points ?? [];
    }
    if (completedPolygons.length === 1) {
      return completedPolygons[0].points;
    }
    return [];
  }, [completedPolygons, drawingPolygonId, isDrawing, selectedPolygonId]);

  const firstDraftVertex = draftVertexPoints.length > 0 ? draftVertexPoints[0] : null;
  const lastDraftVertex = draftVertexPoints.length > 0 ? draftVertexPoints[draftVertexPoints.length - 1] : null;

  const mapCursor = useMemo(() => {
    if (isDrawing) return "crosshair";
    if (isEditing) {
      if (draggingVertexIndex !== null) return "grabbing";
      if (hoveredVertexIndex !== null) return "pointer";
      return "default";
    }
    return "grab";
  }, [draggingVertexIndex, hoveredVertexIndex, isDrawing, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setHoveredVertexIndex(null);
      setDraggingVertexIndex(null);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isDrawing) {
      setDrawCursor(null);
    }
  }, [isDrawing]);

  return (
    <div className="relative h-full w-full">
      <DrawingMapCanvas
        center={mapCenter}
        className="h-full w-full"
        cursor={mapCursor}
        draggable={!isDrawing}
        maxZoom={40}
        minZoom={3}
        onMapClick={(event) => {
          if (!isDrawing) return;
          if (((event.originalEvent as MouseEvent | undefined)?.detail ?? 0) > 1) return;
          if (!event.latlng) return;
          onAddPoint([event.latlng.lat, event.latlng.lng]);
        }}
        onMapDoubleClick={(event) => {
          if (!isDrawing) return;
          L.DomEvent.stopPropagation(event);
          onFinish();
        }}
        onMapMouseMove={(event) => {
          if (!isDrawing) return;
          if (!event.latlng) return;
          setDrawCursor([event.latlng.lat, event.latlng.lng]);
        }}
        onMapMouseOut={() => {
          setDrawCursor(null);
        }}
        zoom={19}
        zoomControl={true}
      >
        {completedPolygons.map((poly) => (
          <PolygonWithoutVertexMarker
            key={poly.id}
            onSelectPolygon={onSelectPolygon}
            poly={poly}
            selectedPolygonId={selectedPolygonId}
          />
        ))}
        {isDrawing && lastDraftVertex && drawCursor ? (
          <Polyline pathOptions={LAYER_RUBBER_BAND} positions={[lastDraftVertex, drawCursor]} />
        ) : null}
        {isDrawing && firstDraftVertex && drawCursor && draftVertexPoints.length > 1 ? (
          <Polyline pathOptions={LAYER_RUBBER_BAND} positions={[drawCursor, firstDraftVertex]} />
        ) : null}
        <PolygonVertexMarkers
          isEditing={isEditing}
          onUpdatePoint={onUpdatePoint}
          points={vertexPoints}
          setDraggingVertexIndex={setDraggingVertexIndex}
          setHoveredVertexIndex={setHoveredVertexIndex}
        />
      </DrawingMapCanvas>
    </div>
  );
};
